import React, { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useSwrUserInterviewList } from '@hooks/useSwrApi/endpoints/interview';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { Button } from '@components/common';
import { ApiSchema } from '@type/api/apiUtilityFn';
import {
  CvDetailInterface,
} from '../interface/CvDetailInterface';

type IInterviewUserQuestionRead = ApiSchema<'InterviewUserQuestionRead'>;

interface ProfileInterviewProps extends CvDetailInterface {
  locale: Locale;
}

export const ProfileInterview = ({ cvDetail, jobSlug, locale }: ProfileInterviewProps) => {
  const { t } = useTranslation('common');
  const { locale: routerLocale } = useExtendedRouter();
  const { isAuth } = useAuthClient();
  const finalLocale = locale || routerLocale || 'ro';

  // Fetch interview list with infinite pagination
  // For CV detail: if jobSlug is provided (applicant view), filter by job; otherwise show all interviews
  // Pass userSlug to fetch interviews for the specific user (CV owner)
  const {
    data,
    size,
    setSize,
    isLoading,
    isEmpty,
    showLoadMore,
  } = useSwrUserInterviewList({
    locale: finalLocale,
    jobSlug: jobSlug || null, // jobSlug - null for general view, jobSlug for applicant view
    userSlug: cvDetail?.slug || null, // userSlug - pass the CV owner's slug to fetch their interviews
    shouldFetch: isAuth, // endpoint requires authentication - skip the request for guests
  });

  // Flatten the paginated data into a single array
  const allInterviews = useMemo(() => {
    if (!data) return [];
    return data.flatMap((page) => page?.items || []);
  }, [data]);

  // Don't render if no interviews and not loading
  if (!isLoading && isEmpty && !allInterviews.length) {
    return null;
  }

  return (
    <>
      {allInterviews.length > 0 && (
        <h3 className="mb-3 text-lg font-bold">{t('cv.detail.mini_interview')}</h3>
      )}

      {allInterviews.map((item: IInterviewUserQuestionRead) => (
        <div key={item.questionId || item.question} className="mb-4 border-b">
          <div className="mb-2 text-sm font-bold">{item.question}</div>

          {/* Text answer */}
          {item.textAnswer?.answerText ? (
            <blockquote className="my-4 ml-1 border-l-4 border-gray-300 pl-4">
              <div className="my-3 text-sm font-normal">{item.textAnswer.answerText}</div>
            </blockquote>
          ) : (
            <blockquote className="my-4 ml-1 border-l-4 border-gray-300 pl-4">
              <div className="my-3 text-sm font-normal text-gray-400">
                {t('cv.detail.interview.no_answer')}
              </div>
            </blockquote>
          )}
        </div>
      ))}

      {/* Load more button */}
      {showLoadMore && (
        <div className="mt-4 text-center">
          <Button
            styling="text"
            color="primary"
            size="sm"
            onClick={() => { void setSize(size + 1); }}
            isLoading={isLoading}
          >
            {t('global.label.load_more')}
          </Button>
        </div>
      )}
    </>
  );
};
